import { useCallback, useState } from "react";
import Cropper from "react-easy-crop";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Slider } from "@/components/ui/slider";
import { Loader2 } from "lucide-react";

/**
 * Crop a source image (data URL or blob URL) and return a JPEG data URL.
 */
async function cropToDataUrl(src, area, outputMax = 800, quality = 0.75) {
  const img = await new Promise((resolve, reject) => {
    const el = new Image();
    el.onload = () => resolve(el);
    el.onerror = reject;
    el.src = src;
  });
  const scale = Math.min(1, outputMax / Math.max(area.width, area.height));
  const w = Math.round(area.width * scale);
  const h = Math.round(area.height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, area.x, area.y, area.width, area.height, 0, 0, w, h);
  return canvas.toDataURL("image/jpeg", quality);
}

export default function ImageCropDialog({ open, source, onCancel, onCropped }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [pixelArea, setPixelArea] = useState(null);
  const [busy, setBusy] = useState(false);

  const onCropComplete = useCallback((_cropped, cropped) => {
    setPixelArea(cropped);
  }, []);

  const done = async () => {
    if (!pixelArea) return;
    setBusy(true);
    try {
      const dataUrl = await cropToDataUrl(source, pixelArea);
      onCropped(dataUrl);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onCancel(); }}>
      <DialogContent className="sm:max-w-lg p-0 gap-0 overflow-hidden" data-testid="image-crop-dialog">
        <DialogHeader className="px-5 pt-5">
          <DialogTitle className="font-display text-2xl">Crop image</DialogTitle>
          <DialogDescription>Drag to reposition · pinch or use the slider to zoom.</DialogDescription>
        </DialogHeader>
        <div className="relative w-full bg-black" style={{ height: 320 }}>
          {source && (
            <Cropper
              image={source}
              crop={crop}
              zoom={zoom}
              aspect={1}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
              restrictPosition
              showGrid={true}
              cropShape="rect"
            />
          )}
        </div>
        <div className="px-5 py-4 space-y-3">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">Zoom</div>
            <Slider
              data-testid="crop-zoom"
              min={1}
              max={3}
              step={0.05}
              value={[zoom]}
              onValueChange={(v) => setZoom(v[0])}
            />
          </div>
        </div>
        <DialogFooter className="px-5 pb-5 gap-2">
          <Button variant="outline" onClick={onCancel} data-testid="crop-cancel" disabled={busy}>Cancel</Button>
          <Button onClick={done} disabled={!pixelArea || busy} data-testid="crop-confirm" className="btn-lift">
            {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Use image
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
